import { useJob } from 'contexts/JobContext';
import React, { createContext, useContext, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { store } from 'store';
import { ErrorHandlingMessage } from 'Utils/ErrorHandlingMessage';
import { SOCKET_STATUS } from 'Common/Constants';
import { FILE_PROCESSING } from 'Common/ToastConstants';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
    // We now depend on the full jobs array from the JobContext
    const { jobs, updateJobStatus } = useJob();
    // Use a ref to store active sockets, keyed by jobId, for efficient access
    const activeSocketsRef = useRef({});

    useEffect(() => {
        const newSockets = { ...activeSocketsRef.current };
        let token = store.getState().userAuth.userinfo.token;
        // Loop through all jobs to manage connections
        jobs.forEach(job => {
            // If a job is pending or in-processed and we don't have a socket for it yet, create one.
            if ((job.status === SOCKET_STATUS.PENDING || job.status === SOCKET_STATUS.INPOROCESS) && !newSockets[job.jobId]) {
                try {
                    const wsBase = (`${process.env.REACT_APP_EOB_PARSER}` || '')
                        .replace('https://', 'wss://')
                        .replace('http://', 'ws://');
                    const socket = new WebSocket(`${wsBase}/wss/job_status/${job.jobId}?token=${encodeURIComponent(token || '')}`);

                    socket.onopen = () => {
                        // WebSocket connection opened
                    };

                    socket.onmessage = (event) => {
                        const message = JSON.parse(event.data);

                        updateJobStatus(message.job_id, message.status);
                        if (message.status === SOCKET_STATUS.POROCESSED) {
                            toast.success(FILE_PROCESSING(message.file_name, SOCKET_STATUS.POROCESSED));
                        } else if (message.status === SOCKET_STATUS.FAILED) {
                            toast.error(FILE_PROCESSING(message.file_name, SOCKET_STATUS.FAILED));
                        }

                        // Proactively close the socket when job finishes
                        if (message.status === SOCKET_STATUS.POROCESSED || message.status === SOCKET_STATUS.FAILED) {
                            try {
                                socket.close();
                            } catch (error) {
                                ErrorHandlingMessage(error);
                            }
                        }
                    };

                    socket.onerror = (error) => {
                        ErrorHandlingMessage(error);
                    };

                    socket.onclose = (event) => {
                        console.log(`WebSocket connection closed for job: ${job.jobId}`, event);
                        // Clean up the socket from our ref when it closes
                        delete activeSocketsRef.current[job.jobId];
                    };

                    newSockets[job.jobId] = socket;
                } catch (error) {
                    ErrorHandlingMessage(error);
                }
            }
        });

        // Update the ref with new connections
        activeSocketsRef.current = newSockets;
    }, [jobs, updateJobStatus]);

    return (
        <WebSocketContext.Provider value={{}}>
            {children}
        </WebSocketContext.Provider>
    );
};

export const useWebSocket = () => useContext(WebSocketContext);
